import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { REPORTING_LE_LENSES_PATH } from '~/consts/reportingLeLensApi';

import { request } from '~/services/common';

import type { LensFilterValues, LensPeriod } from '~/types/leLens';

import { resolveReportingLeLensUrl } from '~/utils/resolveReportingLeLensUrl';

/** One PDF in a scheduled email: a label (it names the file) and the Lens filters it is drawn with. */
export type LensScheduleFilterSet = { label: string; filters: LensFilterValues };

export type LensScheduleRunFile = { label: string; fileName?: string; bytes?: number; error?: string };

export type LensScheduleRun = {
  id: string;
  scheduleId: string;
  runDate: string;
  trigger: 'schedule' | 'test';
  status: 'queued' | 'running' | 'sent' | 'partial' | 'failed';
  files: Array<LensScheduleRunFile>;
  delivery: 'attachments' | 'link' | null;
  error: string | null;
  createdAt: string;
  finishedAt: string | null;
};

export type LensSchedule = {
  id: string;
  lensId: string;
  ownerEmail: string;
  name: string;
  /** 1 to 28, sent at 8am Sydney time. */
  dayOfMonth: number;
  /** Null sends the Lens's own default period. */
  period: LensPeriod | null;
  filterSets: Array<LensScheduleFilterSet>;
  recipients: Array<string>;
  enabled: boolean;
  /** Lets a custom view show figures it marks internal (e.g. vendor revenue). Off unless chosen. */
  includeInternal: boolean;
  recentRuns: Array<LensScheduleRun>;
};

export type LensScheduleInput = Pick<
  LensSchedule,
  'name' | 'dayOfMonth' | 'period' | 'filterSets' | 'recipients' | 'enabled' | 'includeInternal'
>;

const schedulesPath = (lensId: string) => `${REPORTING_LE_LENSES_PATH}/${encodeURIComponent(lensId)}/schedules`;
const schedulePath = (lensId: string, scheduleId: string) =>
  `${schedulesPath(lensId)}/${encodeURIComponent(scheduleId)}`;
const runPath = (lensId: string, runId: string) =>
  `${REPORTING_LE_LENSES_PATH}/${encodeURIComponent(lensId)}/schedule-runs/${encodeURIComponent(runId)}`;

export const lensScheduleQueryKeys = {
  list: (lensId: string) => ['reporting', 'le-lens', 'schedules', lensId] as const,
  run: (lensId: string, runId: string) => ['reporting', 'le-lens', 'schedule-run', lensId, runId] as const,
};

const json = (method: string, body?: unknown) => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  ...(body === undefined ? {} : { body: JSON.stringify(body) }),
});

/** The signed-in person's own schedules on this Lens, with their latest sends. */
export function useLensSchedules(lensId: string | undefined, enabled = true) {
  return useQuery<Array<LensSchedule>>({
    queryKey: lensScheduleQueryKeys.list(lensId ?? ''),
    enabled: Boolean(lensId) && enabled,
    retry: false,
    // A test send finishes in the background; poll while one is on its way.
    refetchInterval: (query) =>
      (query.state.data ?? []).some((schedule) =>
        schedule.recentRuns.some((run) => run.status === 'queued' || run.status === 'running'),
      )
        ? 5000
        : false,
    queryFn: async ({ signal }) => {
      const response = (await request(resolveReportingLeLensUrl(schedulesPath(lensId!)), { signal })) as {
        schedules?: Array<LensSchedule>;
      };
      return response.schedules ?? [];
    },
  });
}

function useScheduleMutation<TInput, TResult>(lensId: string, mutationFn: (input: TInput) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSettled: () => queryClient.invalidateQueries({ queryKey: lensScheduleQueryKeys.list(lensId) }),
  });
}

export function useSaveLensSchedule(lensId: string) {
  return useScheduleMutation(lensId, async ({ id, input }: { id?: string; input: LensScheduleInput }) => {
    const response = (await request(
      resolveReportingLeLensUrl(id ? schedulePath(lensId, id) : schedulesPath(lensId)),
      json(id ? 'PUT' : 'POST', input),
    )) as { schedule: LensSchedule };
    return response.schedule;
  });
}

export function useDeleteLensSchedule(lensId: string) {
  return useScheduleMutation(lensId, async (scheduleId: string) => {
    await request(resolveReportingLeLensUrl(schedulePath(lensId, scheduleId)), { method: 'DELETE' });
  });
}

/** Sends the next email now, to the schedule's owner only. */
export function useSendLensScheduleTest(lensId: string) {
  return useScheduleMutation(lensId, async (scheduleId: string) => {
    const response = (await request(
      resolveReportingLeLensUrl(`${schedulePath(lensId, scheduleId)}/test`),
      json('POST', {}),
    )) as { run: LensScheduleRun };
    return response.run;
  });
}

/** One send and its files, for the link in an email whose PDFs were too large to attach. */
export function useLensScheduleRun(lensId: string, runId: string) {
  return useQuery({
    queryKey: lensScheduleQueryKeys.run(lensId, runId),
    retry: false,
    queryFn: async ({ signal }) =>
      (await request(resolveReportingLeLensUrl(runPath(lensId, runId)), { signal })) as {
        run: LensScheduleRun;
        schedule: { id: string; name: string };
      },
  });
}

/** A fresh five-minute download link for one PDF of a send. */
export async function fetchLensScheduleFileUrl(lensId: string, runId: string, fileIndex: number) {
  return (await request(resolveReportingLeLensUrl(`${runPath(lensId, runId)}/files/${fileIndex}`))) as {
    url: string;
    fileName: string;
    label: string;
  };
}
